class CartRemoveButton extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}
customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  onChange(event) {
    this.updateQuantity(event.target.dataset.index, event.target.value, document.activeElement.getAttribute('name'), event.target.dataset.quantityVariantId);
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  updateQuantity(line, quantity, name, variantId) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((stateText) => {
        const parsedState = JSON.parse(stateText);
        const quantityElement =
          document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');

        if (parsedState.errors) {
          if (quantityElement) quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        this.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
          if (elementToReplace && parsedState.sections[section.section]) {
             elementToReplace.innerHTML = this.getSectionInnerHTML(
               parsedState.sections[section.section],
               section.selector
             );
          }
        });
        
        const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
        let message = '';
        if (items.length === parsedState.items.length && quantityElement && updatedValue !== parseInt(quantityElement.value)) {
          if (typeof updatedValue === 'undefined') {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem =
          document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
            ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
        }

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        if(errors) errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    if (cartStatus) cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      if (cartStatus) cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    if (mainCartItems) mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    if (document.activeElement) document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    if (mainCartItems) mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();
        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}

// ==========================================
// CUSTOM LOGIC: Auto-Add Add-ons, Vouchers & Virtual Nesting
// ==========================================

// ⚙️ THE DICTIONARY
const ADDON_CONFIG = {
  flatLogo: { id: '46196939587828', title: "Logo Set up Fee" },
  varLogo:  { id: '46764302205172', title: "Logo Print Fee" },
  voucher:  { id: '46396120596724', title: "Voucher Fee" },
  paintedLetters: { id: '48791009034484', title: "Painted Letters Fee" },
  fasteners: {
    "pinback": { id: '46400440140020', title: "Fastener: Pinback" },
    "lightweight pinback": { id: '47966907793652', title: "Fastener: Lightweight Pinback" },
    "eco magnet": { id: '46400440172788', title: "Fastener: Eco Magnet" },
    "deluxe magnet": { id: '46400440205556', title: "Fastener: Deluxe Magnet" },
    "long nail w/ military gripper": { id: '46400440238324', title: "Fastener: Long Nail w/ Military Gripper" },
    "long nail w/ rubber gripper": { id: '46400440271092', title: "Fastener: Long Nail w/ Rubber Gripper" },
    "long nail w/ tie tack gripper": { id: '46400440303860', title: "Fastener: Long Nail w/ Tie Tack Gripper" },
    "regular nail w/ military gripper": { id: '46400493682932', title: "Fastener: Regular Nail w/ Military Gripper" },
    "regular nail w/ rubber gripper": { id: '46400493715700', title: "Fastener: Regular Nail w/ Rubber Gripper" },
    "regular nail w/ tie tack gripper": { id: '46400493748468', title: "Fastener: Regular Nail w/ Tie Tack Gripper" },
    "screw": { id: '46400493781236', title: "Fastener: Screw" },
    "flatback": { id: '46400493814004', title: "Fastener: Flatback" },
    "flatback for magnet": { id: '46707192922356', title: "Fastener: Flatback for Magnet" },
    "pocketclip": { id: '46400493846772', title: "Fastener: Pocketclip" }
  }
};

// 🔤 Alternate spellings / legacy names for fastener option values.
// The line-item property value comes straight from the "fasteners" product's
// variant title, so any rename in Shopify would silently break the exact-key
// lookup below. Map every known wording to a canonical ADDON_CONFIG key here.
const FASTENER_ALIASES = {
  "magnet eco": "eco magnet",
  "econo magnet": "eco magnet",
  "economy magnet": "eco magnet",
  "eco-magnet": "eco magnet",
  "magnet deluxe": "deluxe magnet",
  "deluxe-magnet": "deluxe magnet",
  "pin-back": "pinback",
  "pin back": "pinback",
  "lightweight pin-back": "lightweight pinback",
  "lightweight pin back": "lightweight pinback",
  "light weight pinback": "lightweight pinback",
  "pocket clip": "pocketclip",
  "pocket-clip": "pocketclip",
  "flat back": "flatback",
  "flat back for magnet": "flatback for magnet",
  "flatback for magnets": "flatback for magnet",
  "flat back for magnets": "flatback for magnet"
};

// Normalize a raw option value: lowercase, strip punctuation noise, collapse spaces.
function normalizeFastenerValue(val) {
  return String(val == null ? '' : val)
    .toLowerCase()
    .replace(/[‘’“”]/g, '')
    .replace(/[.,;:]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

// Resolve a fastener option value to its ADDON_CONFIG entry, tolerating
// renamed / re-ordered titles ("Eco Magnet" vs "Magnet Eco").
function resolveFastener(val) {
  const clean = normalizeFastenerValue(val);
  if (!clean) return null;

  // 1. Exact match on a canonical key.
  if (ADDON_CONFIG.fasteners[clean]) return ADDON_CONFIG.fasteners[clean];

  // 2. Known alias / legacy wording.
  if (FASTENER_ALIASES[clean] && ADDON_CONFIG.fasteners[FASTENER_ALIASES[clean]]) {
    return ADDON_CONFIG.fasteners[FASTENER_ALIASES[clean]];
  }

  // 3. Word-order-insensitive match (so "Magnet, Deluxe" == "Deluxe Magnet").
  const sortWords = (s) => s.split(' ').filter(Boolean).sort().join(' ');
  const cleanSorted = sortWords(clean);
  for (const key in ADDON_CONFIG.fasteners) {
    if (sortWords(key) === cleanSorted) return ADDON_CONFIG.fasteners[key];
  }
  for (const alias in FASTENER_ALIASES) {
    if (sortWords(alias) === cleanSorted) {
      const target = ADDON_CONFIG.fasteners[FASTENER_ALIASES[alias]];
      if (target) return target;
    }
  }

  // 4. Magnet-specific safety net: "Flatback for Magnet" must be checked before
  // the plain eco/deluxe magnet rules so it is never mistaken for one of them.
  if (clean.indexOf('magnet') !== -1) {
    if (clean.indexOf('flatback') !== -1 || clean.indexOf('flat back') !== -1) {
      return ADDON_CONFIG.fasteners['flatback for magnet'] || null;
    }
    if (clean.indexOf('deluxe') !== -1) {
      return ADDON_CONFIG.fasteners['deluxe magnet'] || null;
    }
    if (clean.indexOf('eco') !== -1 || clean.indexOf('econo') !== -1 || clean.indexOf('economy') !== -1) {
      return ADDON_CONFIG.fasteners['eco magnet'] || null;
    }
  }

  return null;
}

function getAllTrackedIds() {
  let ids = [ADDON_CONFIG.flatLogo.id, ADDON_CONFIG.varLogo.id, ADDON_CONFIG.voucher.id, ADDON_CONFIG.paintedLetters.id];
  Object.values(ADDON_CONFIG.fasteners).forEach(f => ids.push(f.id));
  return ids;
}

function getTitleForId(vId) {
  if (vId === ADDON_CONFIG.flatLogo.id) return ADDON_CONFIG.flatLogo.title;
  if (vId === ADDON_CONFIG.varLogo.id) return ADDON_CONFIG.varLogo.title;
  if (vId === ADDON_CONFIG.voucher.id) return ADDON_CONFIG.voucher.title;
  if (vId === ADDON_CONFIG.paintedLetters.id) return ADDON_CONFIG.paintedLetters.title;
  for (const key in ADDON_CONFIG.fasteners) {
    if (ADDON_CONFIG.fasteners[key].id === vId) return ADDON_CONFIG.fasteners[key].title;
  }
  return "Add-on Fee";
}

function createAndShowPopup() {
  const overlay = document.createElement('div');
  overlay.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.5);z-index:99999;display:flex;align-items:center;justify-content:center;';
  
  const popup = document.createElement('div');
  popup.style.cssText = 'width:300px;height:300px;background:#fff;padding:20px;border-radius:8px;position:relative;display:flex;flex-direction:column;justify-content:center;align-items:center;text-align:center;box-sizing:border-box;box-shadow:0 4px 15px rgba(0,0,0,0.2);';
  
  const closeX = document.createElement('span');
  closeX.innerHTML = '&times;';
  closeX.style.cssText = 'position:absolute;top:15px;right:15px;font-size:24px;cursor:pointer;line-height:1;font-weight:bold;color:#333;';
  
  const text = document.createElement('p');
  text.textContent = 'Items requiring a setup fee have been added to your cart.';
  text.style.cssText = 'margin:0 0 25px 0;font-size:16px;line-height:1.5;color:#000;';
  
  const closeBtn = document.createElement('button');
  closeBtn.textContent = 'Close';
  closeBtn.style.cssText = 'padding:10px 24px;background:#000;color:#fff;border:none;border-radius:4px;cursor:pointer;font-size:16px;font-weight:bold;transition:background 0.2s;';
  closeBtn.onmouseover = () => closeBtn.style.background = '#333';
  closeBtn.onmouseout = () => closeBtn.style.background = '#000';

  const closePopup = () => overlay.remove();
  closeX.onclick = closePopup;
  closeBtn.onclick = closePopup;
  overlay.onclick = (e) => { if (e.target === overlay) closePopup(); };

  popup.appendChild(closeX);
  popup.appendChild(text);
  popup.appendChild(closeBtn);
  overlay.appendChild(popup);
  document.body.appendChild(overlay);
}

// ⚡ VIRTUAL NESTING
function virtualNestCartItems(docContext = document) {
  const trackedIds = getAllTrackedIds();
  const items = docContext.querySelectorAll('.cart-item');
  
  items.forEach(item => {
    const qtyInput = item.querySelector('.quantity__input');
    if (!qtyInput) return;
    
    const vId = qtyInput.dataset.quantityVariantId || qtyInput.getAttribute('data-quantity-variant-id');
    
    if (trackedIds.includes(vId)) {
      
      let badgeTitle = getTitleForId(vId);

      item.style.display = 'none';
      item.style.visibility = 'hidden';
      item.style.position = 'absolute'; 
      
      let linkType = "";
      let linkValue = "";
      
      item.querySelectorAll('.product-option').forEach(opt => {
         const dt = opt.querySelector('dt');
         const dd = opt.querySelector('dd');
         if (dt && dd) {
             const label = dt.textContent.toLowerCase();
             const val = dd.textContent.trim().toLowerCase();
             
             if (label.includes('for part number')) {
                 linkType = "partNumber";
                 linkValue = val;
             } else if (label.includes('for variant')) {
                 linkType = "variant";
                 linkValue = val;
             }
         }
      });

      const priceWrap = item.querySelector('.cart-item__price-wrapper .price') || item.querySelector('.price');
      let feePrice = priceWrap ? priceWrap.innerHTML.trim() : '';
      if (feePrice !== '') feePrice = `+ ${feePrice}`;

      if (linkValue !== "") {
         items.forEach(parent => {
           if (parent === item) return;
           
           let isMatch = false;

           if (linkType === "partNumber") {
               let parentPartNum = "";
               parent.querySelectorAll('.product-option').forEach(opt => {
                 const dt = opt.querySelector('dt');
                 const dd = opt.querySelector('dd');
                 if (dt && dd) {
                    const label = dt.textContent.toLowerCase();
                    if (label.includes('part number') && !label.includes('for part number')) {
                       parentPartNum = dd.textContent.trim().toLowerCase();
                    }
                 }
               });
               if (parentPartNum === linkValue) isMatch = true;
               
           } else if (linkType === "variant") {
               const pQtyInput = parent.querySelector('.quantity__input');
               if (pQtyInput) {
                   const pVId = pQtyInput.dataset.quantityVariantId || pQtyInput.getAttribute('data-quantity-variant-id');
                   if (pVId && pVId.toLowerCase() === linkValue) {
                       isMatch = true;
                   }
               }
           }
           
           if (isMatch) {
              const detailsCol = parent.querySelector('.cart-item__details');
              
              if (detailsCol && !parent.querySelector(`.virtual-fee-${vId}`)) {
                 detailsCol.style.textAlign = 'left';
                 detailsCol.style.alignItems = 'flex-start';

                 const feeBadge = document.createElement('div');
                 feeBadge.className = `virtual-fee-${vId}`;
                 
                 feeBadge.style.cssText = 'margin-top: 0.8rem; padding: 0.8rem 1.2rem; background: #f8fafc; border-radius: 0.6rem; border-left: 4px solid #cbd5e1; display: flex; justify-content: space-between; align-items: center; box-shadow: inset 0 1px 3px rgba(0,0,0,0.03); width: 100%; max-width: 320px; box-sizing: border-box;';
                 
                 feeBadge.innerHTML = `
                   <div style="display:flex; align-items:center; gap:0.6rem; font-size:1.2rem; line-height:1;">
                     <span style="color:#94a3b8; font-weight:bold; transform: translateY(-2px);">↳</span>
                     <strong style="color:#334155;">${badgeTitle}</strong>
                   </div>
                   <div style="font-size:1.2rem; font-weight:bold; color:#0f172a; margin-left: 1rem;">
                     ${feePrice}
                   </div>
                 `;
                 detailsCol.appendChild(feeBadge);
              }
           }
         });
      }
    }
  });
}

function refreshCartUI() {
  let promises = [];
  
  if (document.querySelector('cart-drawer-items')) {
    promises.push(fetch(`${window.Shopify?.routes?.root || '/'}cart?section_id=cart-drawer`)
      .then(res => res.text())
      .then(text => {
        const html = new DOMParser().parseFromString(text, 'text/html');
        virtualNestCartItems(html); 
        ['cart-drawer-items', '.cart-drawer__footer'].forEach(selector => {
          const target = document.querySelector(selector);
          const source = html.querySelector(selector);
          if (target && source) target.replaceWith(source);
        });
      }));
  }
  
  if (document.querySelector('cart-items') && !document.querySelector('cart-drawer-items')) {
    promises.push(fetch(`${window.Shopify?.routes?.root || '/'}cart?section_id=main-cart-items`)
      .then(res => res.text())
      .then(text => {
        const html = new DOMParser().parseFromString(text, 'text/html');
        virtualNestCartItems(html); 
        const target = document.querySelector('cart-items');
        const source = html.querySelector('cart-items');
        if (target && source) target.innerHTML = source.innerHTML;
      }));
  }

  promises.push(fetch(`${window.Shopify?.routes?.root || '/'}cart?section_id=cart-icon-bubble`)
    .then(res => res.text())
    .then(text => {
      const html = new DOMParser().parseFromString(text, 'text/html');
      const target = document.querySelector('#cart-icon-bubble');
      const source = html.querySelector('#cart-icon-bubble');
      if (target && source) target.innerHTML = source.innerHTML;
    }));

  return Promise.all(promises).then(function () { applyLineFeeTotals(); });
}

// 💰 Show each parent line's TOTAL including its nested add-on fees.
// Fees are separate (hidden) lines, so read accurate cents from cart.js and
// write parent line_total + matched fee totals into the parent's total cell.
function parseCurrencySymbol() {
  var el = document.querySelector('.cart-item__totals .price, .cart-item__price-wrapper .price, .price');
  if (el) {
    var m = (el.textContent || '').match(/^[^\d\-]+/);
    if (m) return m[0].trim();
  }
  return '$';
}
function formatCents(cents, symbol) {
  return (symbol || '$') + (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function applyLineFeeTotals() {
  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  fetch(root + 'cart.js')
    .then(function (r) { return r.json(); })
    .then(function (cart) {
      var trackedIds = getAllTrackedIds();
      var feeByPart = {}, feeByVariant = {};
      cart.items.forEach(function (it) {
        if (trackedIds.indexOf(String(it.variant_id)) === -1) return;
        var p = it.properties || {};
        if (p["For Part Number"]) {
          var k = String(p["For Part Number"]).toLowerCase();
          feeByPart[k] = (feeByPart[k] || 0) + it.final_line_price;
        } else if (p["For Variant"]) {
          var kv = String(p["For Variant"]);
          feeByVariant[kv] = (feeByVariant[kv] || 0) + it.final_line_price;
        }
      });
      var symbol = parseCurrencySymbol();
      cart.items.forEach(function (it, idx) {
        if (trackedIds.indexOf(String(it.variant_id)) !== -1) return; // skip fee lines
        var p = it.properties || {};
        var extra = 0;
        var pn = p["Part number"] || p["Part number:"];
        if (pn && feeByPart[String(pn).toLowerCase()]) extra += feeByPart[String(pn).toLowerCase()];
        if (feeByVariant[String(it.variant_id)]) extra += feeByVariant[String(it.variant_id)];
        if (extra <= 0) return;
        var lineNo = idx + 1;
        var row = document.getElementById('CartItem-' + lineNo) || document.getElementById('CartDrawer-Item-' + lineNo);
        if (!row) return;
        var newText = formatCents(it.final_line_price + extra, symbol);
        // The row has two totals cells (mobile + desktop, one hidden per breakpoint),
        // so update every one. Write into the innermost price span to preserve any
        // currency-converter markup.
        var cells = row.querySelectorAll('.cart-item__totals');
        if (!cells.length) cells = row.querySelectorAll('.cart-item__price-wrapper');
        cells.forEach(function (cell) {
          var priceEls = cell.querySelectorAll('.cart-item__price-wrapper .price, .price');
          var priceEl = priceEls.length ? priceEls[priceEls.length - 1] : null;
          if (!priceEl) return;
          var inner = priceEl.querySelector('span') || priceEl;
          if (inner.textContent !== newText) inner.textContent = newText;
        });
      });
    })
    .catch(function () {});
}

// 🧠 THE BRAIN: Scans cart and builds the perfect add-on list
function syncAllAddons() {
  const trackedIds = getAllTrackedIds();

  fetch(window.Shopify?.routes?.root + 'cart.js' || '/cart.js')
    .then(res => res.json())
    .then(cart => {
       let requiredItems = []; 
       let existingItems = []; 
       
       cart.items.forEach((item, index) => {
         const isTracked = trackedIds.includes(String(item.variant_id));
         
         if (isTracked) {
           let linkKey = "";
           let linkVal = "";
           
           if (item.properties && item.properties["For Part Number"]) {
             linkKey = "For Part Number";
             linkVal = item.properties["For Part Number"];
           } else if (item.properties && item.properties["For Variant"]) {
             linkKey = "For Variant";
             linkVal = item.properties["For Variant"];
           }
           
           existingItems.push({ key: item.key, variant_id: String(item.variant_id), quantity: item.quantity, linkKey: linkKey, linkValue: linkVal });
           return; 
         }

         if (item.properties) {
            let partNum = item.properties["Part number"] || item.properties["Part number:"] || `Line-${index+1}`; 
            let parentVariantId = String(item.variant_id); 
            
            let needsFlat = false;
            let needsVar = false;
            let needsVoucher = false;
            let needsFastenerId = null;
            let needsPaintedLetters = false;

            for (const [key, value] of Object.entries(item.properties)) {
               const cleanKey = key.toLowerCase().trim();
               const cleanVal = String(value).toLowerCase().trim();
               
               if (cleanKey === "does reeves have your logo?" && cleanVal === "no") needsFlat = true;
               if (cleanKey === "need logo?" && cleanVal === "yes") needsVar = true;
               if (cleanKey === "vouchers" && cleanVal === "yes") needsVoucher = true; 
               if (cleanKey.indexOf("color-filled") !== -1 && cleanVal === "yes") needsPaintedLetters = true;
               
               if (cleanKey === "fastener:" || cleanKey === "fastener") {
                 const matchedFastener = resolveFastener(cleanVal);
                 if (matchedFastener) {
                   needsFastenerId = matchedFastener.id;
                 } else if (cleanVal && cleanVal !== "none" && cleanVal !== "none selected") {
                   console.warn('[Reeves] Unrecognized fastener option value, no fee added:', value);
                 }
               }
            }

            // ── Voucher lines always carry the Logo Print Fee ──────────────
            // A voucher order always has a logo imprinted at creation — that
            // is the whole point of the flow — so the per-unit print fee is
            // unconditional here rather than driven by a question.
            //
            // It deliberately does NOT read "Need logo?": that property
            // belongs to the regular Buy Now flow, and having the voucher
            // flow write it too would mean two flows sharing one trigger,
            // where a change to either silently moves the other's pricing.
            //
            // The Logo Set up Fee below is left on its own trigger
            // ("Does Reeves have your logo?" === "no"), which the voucher
            // flow's first question writes directly. A customer who has just
            // told us the logo is already on file should not be charged to
            // set it up again.
            if (needsVoucher) needsVar = true;

            if (needsFlat) requiredItems.push({ id: ADDON_CONFIG.flatLogo.id, qty: 1, linkKey: "For Part Number", linkValue: partNum });
            if (needsVar) requiredItems.push({ id: ADDON_CONFIG.varLogo.id, qty: item.quantity, linkKey: "For Part Number", linkValue: partNum });
            if (needsFastenerId) requiredItems.push({ id: needsFastenerId, qty: item.quantity, linkKey: "For Part Number", linkValue: partNum });
            if (needsVoucher) requiredItems.push({ id: ADDON_CONFIG.voucher.id, qty: item.quantity, linkKey: "For Variant", linkValue: parentVariantId });
            if (needsPaintedLetters) requiredItems.push({ id: ADDON_CONFIG.paintedLetters.id, qty: item.quantity, linkKey: "For Part Number", linkValue: partNum });
         }
       });

       let updates = {}; 
       let adds = [];    
       let needsPopup = false;
       
       requiredItems.forEach(req => {
         let foundIndex = existingItems.findIndex(ex => ex.variant_id === req.id && ex.linkValue === req.linkValue);
         
         if (foundIndex !== -1) {
           let ex = existingItems[foundIndex];
           if (ex.quantity !== req.qty) updates[ex.key] = req.qty; 
           existingItems.splice(foundIndex, 1); 
         } else {
           let properties = {};
           if (req.linkValue && !req.linkValue.startsWith("Line-")) properties[req.linkKey] = req.linkValue;
           adds.push({ id: req.id, quantity: req.qty, properties: properties });
           
           if (req.id === ADDON_CONFIG.flatLogo.id || req.id === ADDON_CONFIG.varLogo.id) {
             needsPopup = true;
           }
         }
       });

       existingItems.forEach(ex => updates[ex.key] = 0);

       if (Object.keys(updates).length > 0 || adds.length > 0) {
         let sequence = Promise.resolve();
         
         if (Object.keys(updates).length > 0) {
           sequence = sequence.then(() => 
             fetch(window.Shopify?.routes?.root + 'cart/update.js', {
               method: 'POST',
               headers: { 'Content-Type': 'application/json' },
               body: JSON.stringify({ updates: updates })
             })
           );
         }

         if (adds.length > 0) {
           sequence = sequence.then(() => 
             fetch(window.Shopify?.routes?.root + 'cart/add.js', {
               method: 'POST',
               headers: { 'Content-Type': 'application/json' },
               body: JSON.stringify({ items: adds })
             })
           ).then(() => {
             if (needsPopup) createAndShowPopup();
           });
         }

         sequence.then(() => refreshCartUI()).catch(e => console.error(e));
       } else {
         virtualNestCartItems(document);
         applyLineFeeTotals();
       }
    })
    .catch(error => console.error(error));
}

// Hide add-on fee rows with persistent CSS so they never flash in as a separate
// line, regardless of how often the cart re-renders. The :has() rule lives in
// <head> and applies the instant a fee row exists (no JS timing window);
// virtualNestCartItems still adds the nested badge to the parent line.
function injectFeeHideCSS() {
  var ids = getAllTrackedIds();
  if (!ids.length) return;
  var sel = ids.map(function (id) {
    return '.cart-item:has(.quantity__input[data-quantity-variant-id="' + id + '"])';
  }).join(',');
  var el = document.getElementById('reeves-fee-hide-style');
  if (!el) {
    el = document.createElement('style');
    el.id = 'reeves-fee-hide-style';
    (document.head || document.documentElement).appendChild(el);
  }
  el.textContent = sel + '{display:none !important;}';
}
injectFeeHideCSS();

document.addEventListener("DOMContentLoaded", function () {
  injectFeeHideCSS();
  syncAllAddons();
});

if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
  subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
    setTimeout(syncAllAddons, 10); 
  });
}
// ==========================================